import { useEffect, useMemo } from 'react';
import { Typography } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import EmployeeShiftCard from '../../../EmployeeShiftCard/EmployeeShiftCard';
import {
  StyledTableCell,
  StyledShiftBox,
  StyledTableRow,
  StyledTableTitle,
  StyledTime,
  EmptyShift,
} from './ShiftRow.styles';
import { isPastDay, isSameDay } from '../../../../utils/dateUtils';
import CustomTextButton from '../../../CustomTextButton/CustomTextButton';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import { calculateTotalHours, organizeShiftsByDay } from '../../../../utils/helpers/shifts/shiftsHelpers';
import { SHIFT_PUBLISH_STATUS } from '../../../../utils/constants/shiftConstants';
import { useTimeFormatters } from '../../../../utils/Hooks/venue/useVenueTimeFormatters';
import { useSelector } from 'react-redux';
import { selectCurrentVenue } from '../../../../redux/slices/currentUserSlice';
import { getVenueTimezone } from '../../../../utils/venueTimeFormatters';

const ShiftRow = ({
  role,
  assigned,
  shifts,
  name,
  image,
  daysOfWeek,
  onShiftClick,
  selectedShift,
  onAddShift,
  dateFromShiftButton,
  onDateFromShiftButtonChange,
}) => {
  // Use TZ-aware formatters that automatically apply venue timezone
  const { formatShiftTime } = useTimeFormatters();

  // Calculates the total hours worked by the employee
  const totalHours = calculateTotalHours(shifts);

  const currentVenue = useSelector(selectCurrentVenue);
  const venueTimezone = getVenueTimezone(currentVenue);

  // Organize shifts by day for efficient access
  // daysOfWeek is an array of objects representing the days/dates of the week starting with the venue's start of week
  // the format is: [{ day: 'Mon', date: 1732137600000 }, { day: 'Tue', date: 1732224000000 }, ...]
  // returns an object with the day abbreviations as the keys and the shifts for that day as the value
  const shiftsByDay = useMemo(() => {
    return organizeShiftsByDay(shifts, daysOfWeek, venueTimezone);
  }, [shifts, daysOfWeek, venueTimezone]);

  // Open the drawer to add a new shift when the button is clicked
  useEffect(() => {
    if (dateFromShiftButton) {
      onAddShift();
    }
  }, [dateFromShiftButton]); // eslint-disable-line

  return (
    <StyledTableRow>
      {/* Cell of the title for each employee */}
      <StyledTableTitle component="td">
        <div>
          <EmployeeShiftCard
            role={role}
            assigned={assigned}
            name={name}
            totalHours={`${totalHours} hrs`}
            assignedEmployee={
              assigned
                ? {
                    name,
                    profileImageUrl: image,
                  }
                : null
            }
          />
        </div>
      </StyledTableTitle>

      {/* Cells for the week */}
      {daysOfWeek?.map((dayObj, index) => {
        // get the shifts for this day
        const dayAbbreviation = dayObj.day;
        const shiftsForDay = shiftsByDay[dayAbbreviation] || [];

        // validate if this day is the current day
        const isCurrentDay = isSameDay(dayObj.date, new Date());

        // validate if this day is in the past
        const isDayInPast = isPastDay(dayObj.date);

        return (
          <StyledTableCell key={index}>
            {shiftsForDay.length > 0 ? (
              <>
                {/* Render each shift for this day */}
                {shiftsForDay.map((shift, idx) => {
                  // Determine position classes
                  const positionClass =
                    shiftsForDay.length === 1
                      ? 'single'
                      : idx === 0
                        ? 'first'
                        : idx === shiftsForDay.length - 1 && (assigned || isDayInPast)
                          ? 'last'
                          : '';

                  // Determine state classes
                  const stateClasses = [
                    positionClass,
                    isCurrentDay ? 'current-day' : '',
                    selectedShift && selectedShift.id === shift.id ? 'selected' : '',
                    shift.isPublished ? SHIFT_PUBLISH_STATUS.PUBLISHED : SHIFT_PUBLISH_STATUS.UNPUBLISHED,
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <StyledShiftBox key={shift.id} onClick={() => onShiftClick(shift)} className={stateClasses}>
                      <StyledTime variant="body2">
                        {formatShiftTime(shift)}
                        {shift.conflicts?.length > 0 && <WarningIcon fontSize="small" />}
                      </StyledTime>
                      <Typography variant="bodyRegular">{shift.name}</Typography>
                    </StyledShiftBox>
                  );
                })}

                {/* Button to add shift (only for unassigned roles and future days) */}
                {!assigned && !isDayInPast && (
                  <StyledShiftBox className="last">
                    <CustomTextButton
                      text="Shift"
                      icon={AddBoxOutlinedIcon}
                      onClick={() => {
                        onDateFromShiftButtonChange(new Date(dayObj.date));
                      }}
                      isUsingParentBackground={true}
                    />
                  </StyledShiftBox>
                )}
              </>
            ) : (
              <EmptyShift>-</EmptyShift>
            )}
          </StyledTableCell>
        );
      })}
    </StyledTableRow>
  );
};

export default ShiftRow;
